import styled from "@emotion/styled";
import { Box } from "@mui/material";
import {
  createLinearGradient,
  createSchemeAwareShadedGradient
} from "@themes/utils/gradients";
import { PROGRESS_BAR_HEIGHT } from "./progressBar.constants";

/**
 * Progress track + fill primitives. Extracted from BaseTable's component grab
 * bag so molecules (e.g. MetricCard) can use them without importing organisms.
 */
export const ProgressBarBackground = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "borderRadius"
})<{ height?: number; borderRadius?: number }>(
  ({ borderRadius = 9999, height = PROGRESS_BAR_HEIGHT, theme }) => ({
    background: createLinearGradient(
      theme.vars.palette.grey[300],
      theme.vars.palette.grey[400]
    ),
    borderRadius: `${borderRadius}px`,
    flex: 1,
    maxHeight: `${height}px`,
    minHeight: `${height}px`,
    // Anchors {@link ProgressBarMarker}, which is positioned along the track.
    position: "relative",
    width: "100%"
  })
);

export const ProgressBarMarker = styled(Box, {
  shouldForwardProp: (prop) => prop !== "at" && prop !== "height"
})<{ at: number; height?: number }>(
  ({ at, height = PROGRESS_BAR_HEIGHT, theme }) => ({
    background: theme.vars.palette.text.primary,
    borderRadius: "1px",
    // Stands a little proud of the track so it reads as a threshold marker
    // rather than a gap in the fill.
    height: `${height + 4}px`,
    left: `${Math.min(100, Math.max(0, at))}%`,
    position: "absolute",
    top: "-2px",
    transform: "translateX(-50%)",
    width: "2px"
  })
);

export const ProgressBarFill = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "progress" &&
    prop !== "color" &&
    prop !== "height" &&
    prop !== "borderRadius"
})<{
  progress: number;
  color?: string;
  height?: number;
  borderRadius?: number;
}>(({
  borderRadius = 9999,
  color,
  height = PROGRESS_BAR_HEIGHT,
  progress,
  theme
}) => {
  const fallbackColor =
    progress >= 100
      ? theme.vars.palette.success.main
      : theme.vars.palette.warning.main;

  return {
    ...createSchemeAwareShadedGradient(theme, color ?? fallbackColor),
    borderRadius: `${borderRadius}px`,
    height: `${height}px`,
    width: `${progress}%`
  };
});
